import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import React, { useContext, useState, useRef, cloneElement, isValidElement } from 'react';
import { safeInvoke } from '../utils';
import ListOperationContext from './ListOperationContext';
import Popover from '../Popover';
import Translate from '../Translate';
import Button from '../Button';
import Media from '../Media';
import Icon from '../Icon';
import Text from '../Text';
import Paragraph from '../Paragraph';

var toBold = function toBold(value) {
  return value === undefined ? undefined : React.createElement(Text, {
    bold: true
  }, value);
};

var getDefaultText = function getDefaultText(key) {
  var fallback;

  switch (key) {
    case 'cancelButton':
      fallback = 'Cancel';
      break;

    default:
      throw new Error('Unknown key');
  }

  var content = "ListOperation.".concat(key);
  return React.createElement(Translate, {
    content: content,
    fallback: fallback
  });
};
/**
 * `ListOperation` component is used in [Toolbar](#!/Toolbar) for performing
 * operations on multiple objects in [List](#!/List).
 * @since 2.4.0
 */


var ListOperation = function ListOperation(_ref) {
  var _ref$critical = _ref.critical,
      critical = _ref$critical === void 0 ? false : _ref$critical,
      noSelectionText = _ref.noSelectionText,
      confirmationText = _ref.confirmationText,
      actionButtonText = _ref.actionButtonText,
      cancelButtonText = _ref.cancelButtonText,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, ["critical", "noSelectionText", "confirmationText", "actionButtonText", "cancelButtonText", "children"]);

  var _useContext = useContext(ListOperationContext),
      selectedRows = _useContext.selectedRows,
      selectedRowTitle = _useContext.selectedRowTitle;

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      settings = _useState2[0],
      setSettings = _useState2[1];

  var targetRef = useRef();

  if (!isValidElement(children)) {
    throw new Error('The "children" property must be an element.');
  }

  var toText = function toText(prop) {
    if (typeof prop === 'function') {
      return prop({
        selectedRows: toBold(selectedRows),
        selectedRowTitle: toBold(selectedRowTitle)
      });
    }

    return prop;
  };

  var handleClick = function handleClick(e) {
    targetRef.current = e.currentTarget;

    switch (true) {
      case selectedRows && critical:
        setSettings({
          message: toText(confirmationText),
          actionButtonText: toText(actionButtonText),
          actionButtonIntent: 'danger',
          cancelButtonText: toText(cancelButtonText) || getDefaultText('cancelButton')
        });
        break;

      case !selectedRows && !critical:
        setSettings({
          message: toText(noSelectionText),
          actionButtonText: toText(actionButtonText),
          actionButtonIntent: 'primary',
          cancelButtonText: toText(cancelButtonText) || getDefaultText('cancelButton')
        });
        break;

      case !selectedRows && critical:
        setSettings({
          message: toText(noSelectionText),
          icon: React.createElement(Icon, {
            intent: "info",
            name: "info-circle-filled"
          })
        });
        break;

      case selectedRows && !critical:
        safeInvoke(children.props.onClick, e);
        break;
    }
  };

  var handleCancel = function handleCancel() {
    setSettings(null);
  };

  var handleAction = function handleAction(e) {
    setSettings(null);
    safeInvoke(children.props.onClick, e);
  };

  var target = cloneElement(children, _objectSpread({}, props, {
    onClick: handleClick
  }));
  return React.createElement(Popover, {
    visible: !!settings,
    target: target,
    targetRef: targetRef,
    placement: "top",
    onClose: handleCancel
  }, settings && React.createElement(Media, {
    image: settings.icon
  }, React.createElement(Paragraph, null, settings.message), settings.actionButtonText && React.createElement("div", null, React.createElement(Button, {
    onClick: handleAction,
    intent: settings.actionButtonIntent
  }, settings.actionButtonText), " ", React.createElement(Button, {
    ghost: true,
    onClick: handleCancel
  }, settings.cancelButtonText))));
};

export default ListOperation;